import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, X, ArrowUp, ArrowDown, Image as ImageIcon, Upload, Gear } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  eritema: 'Eritema (Rojeces)',
  descamacion: 'Descamación',
  sequedad: 'Sequedad',
  edema: 'Edema',
  prurito: 'Prurito (Picor)',
  pigmentacion: 'Pigmentación',
  arrugas: 'Arrugas',
  flacidez: 'Flacidez',
  otros: 'Otros',
};
const categoryColors = {
  eritema: 'bg-red-50 text-red-700 border-red-200',
  descamacion: 'bg-amber-50 text-amber-700 border-amber-200',
  sequedad: 'bg-orange-50 text-orange-700 border-orange-200',
  edema: 'bg-blue-50 text-blue-700 border-blue-200',
  prurito: 'bg-pink-50 text-pink-700 border-pink-200',
  pigmentacion: 'bg-violet-50 text-violet-700 border-violet-200',
  arrugas: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  flacidez: 'bg-teal-50 text-teal-700 border-teal-200',
  otros: 'bg-stone-50 text-stone-600 border-stone-200',
};

/**
 * Dermatological Scales catalog — URS-F214
 * Reusable clinical scales (Eritema, Descamacion, etc.) with reference image.
 */
export function DermScalesPanel({ lang }) {
  const [scales, setScales] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'eritema', description: '', file_id: null, options: [], response_mode: 'select', numeric_prompt: '', numeric_unit: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(null);
  const [showCategoriesDialog, setShowCategoriesDialog] = useState(false);

  const load = () => {
    Promise.all([
      axios.get(`${API_URL}/api/config/derm-scales`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/derm-scale-categories`, { withCredentials: true }),
    ]).then(([sr, cr]) => {
      setScales(sr.data || []);
      setCategories(cr.data || []);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      category: categories[0] || 'eritema',
      description: '',
      file_id: null,
      response_mode: 'select',
      numeric_prompt: '',
      numeric_unit: '',
      options: [
        { value: '0', label: '', description: '' },
        { value: '1', label: '', description: '' },
      ],
    });
    setShowDialog(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      category: s.category || 'otros',
      description: s.description || '',
      file_id: s.file_id || null,
      response_mode: s.response_mode || 'select',
      numeric_prompt: s.numeric_prompt || '',
      numeric_unit: s.numeric_unit || '',
      options: (s.options || []).map(o => ({ value: o.value, label: o.label, description: o.description || '' })),
    });
    setShowDialog(true);
  };

  const addOption = () => {
    setForm(p => ({
      ...p,
      options: [...p.options, { value: String(p.options.length), label: '', description: '' }],
    }));
  };
  const updateOption = (idx, key, val) => setForm(p => ({
    ...p,
    options: p.options.map((o, i) => i === idx ? { ...o, [key]: val } : o),
  }));
  const removeOption = (idx) => setForm(p => ({
    ...p,
    options: p.options.filter((_, i) => i !== idx),
  }));
  const moveOption = (idx, delta) => setForm(p => {
    const j = idx + delta;
    if (j < 0 || j >= p.options.length) return p;
    const arr = [...p.options];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, options: arr };
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === 'es' ? 'Imagen máx 5 MB' : 'Image max 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/files/upload`, fd, { withCredentials: true });
      setForm(p => ({ ...p, file_id: data.id }));
      toast.success(lang === 'es' ? 'Imagen subida' : 'Image uploaded');
    } catch (err) {
      toast.error(errText(err));
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'Nombre obligatorio' : 'Name required'); return; }
    // URS-F510: validacion solo si es escala de seleccion
    if (form.response_mode !== 'numeric') {
      if (form.options.length < 2) { toast.error(lang === 'es' ? 'Mínimo 2 opciones' : 'Min 2 options'); return; }
      if (form.options.some(o => !o.value.trim() || !o.label.trim())) {
        toast.error(lang === 'es' ? 'Cada opción necesita valor y etiqueta' : 'Each option needs value and label');
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/config/derm-scales/${editing.scale_id}`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Escala actualizada' : 'Scale updated');
      } else {
        await axios.post(`${API_URL}/api/config/derm-scales`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Escala creada' : 'Scale created');
      }
      setShowDialog(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/config/derm-scales/${id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Eliminada' : 'Deleted');
      setConfirmDel(null); load();
    } catch (err) { toast.error(errText(err)); }
  };

  const filtered = filterCat === 'all' ? scales : scales.filter(s => s.category === filterCat);

  return (
    <div className="space-y-4" data-testid="derm-scales-panel">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-stone-500 max-w-2xl">
          {lang === 'es'
            ? 'Catálogo de escalas dermatológicas reutilizables (Eritema, Descamación, Sequedad, Edema, Prurito, Pigmentación). Cada escala tiene una imagen de referencia clínica que se mostrará en un popup durante la cumplimentación del cuestionario.'
            : 'Reusable dermatological scales catalog with reference image.'}
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowCategoriesDialog(true)} data-testid="derm-categories-btn" className="rounded-sm gap-2">
            <Gear size={14} />{lang === 'es' ? 'Categorías' : 'Categories'}
          </Button>
          <Button onClick={openCreate} data-testid="derm-scale-new-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
            <Plus size={16} />{lang === 'es' ? 'Nueva escala' : 'New scale'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-48 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === 'es' ? 'Todas las categorías' : 'All categories'}</SelectItem>
            {categories.map(c => (<SelectItem key={c} value={c}>{categoryLabels[c] || c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs font-mono">{filtered.length}</Badge>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-sm text-stone-500">
          {lang === 'es' ? 'Sin escalas dermatológicas configuradas' : 'No scales yet'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(s => (
            <div key={s.scale_id} data-testid={`derm-scale-card-${s.scale_id}`} className="bg-white border border-stone-200 rounded-sm overflow-hidden flex">
              <button
                type="button"
                onClick={() => s.file_id && setShowImagePreview(s)}
                className={`w-32 h-32 shrink-0 flex items-center justify-center border-r border-stone-200 ${s.file_id ? 'cursor-zoom-in hover:bg-stone-50' : 'bg-stone-50'}`}
                title={s.file_id ? (lang === 'es' ? 'Ver imagen' : 'View image') : (lang === 'es' ? 'Sin imagen' : 'No image')}
              >
                {s.file_id ? (
                  <img
                    src={`${API_URL}/api/public/derm-scales/${s.scale_id}/image`}
                    alt={s.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageIcon size={28} className="text-stone-300" />
                )}
              </button>
              <div className="flex-1 p-3 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-stone-900 truncate">{s.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline" className={`text-[10px] ${categoryColors[s.category] || categoryColors.otros}`}>
                        {categoryLabels[s.category] || s.category}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-mono">{(s.options || []).length} {lang === 'es' ? 'niveles' : 'levels'}</Badge>
                    </div>
                    {s.description && <p className="text-[11px] text-stone-500 mt-1.5 line-clamp-2">{s.description}</p>}
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`derm-scale-edit-${s.scale_id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setConfirmDel(s)} data-testid={`derm-scale-delete-${s.scale_id}`}><Trash size={13} /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(s.options || []).map((o, i) => (
                    <span key={i} className="bg-stone-100 border border-stone-200 rounded px-1.5 text-[10px] text-stone-600 font-mono">
                      {o.value}: <span className="font-sans">{o.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image preview popup */}
      <Dialog open={!!showImagePreview} onOpenChange={v => { if (!v) setShowImagePreview(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{showImagePreview?.name}</DialogTitle>
            <DialogDescription className="text-xs">{showImagePreview?.description}</DialogDescription>
          </DialogHeader>
          {showImagePreview?.file_id && (
            <div className="flex items-center justify-center bg-stone-50 rounded-sm overflow-auto" style={{ maxHeight: '70vh' }}>
              <img
                src={`${API_URL}/api/public/derm-scales/${showImagePreview.scale_id}/image`}
                alt={showImagePreview.name}
                className="max-w-full"
                data-testid="derm-scale-preview-image"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editing ? (lang === 'es' ? 'Editar escala' : 'Edit scale') : (lang === 'es' ? 'Nueva escala' : 'New scale')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>{lang === 'es' ? 'Nombre de la escala' : 'Scale name'} <span className="text-red-500">*</span></Label>
                <Input data-testid="derm-scale-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Escala de Rojeces (Eritema)" />
              </div>
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Categoría' : 'Category'}</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="derm-scale-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (<SelectItem key={c} value={c}>{categoryLabels[c] || c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Descripción (opcional)' : 'Description (optional)'}</Label>
              <Textarea data-testid="derm-scale-desc-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder={lang === 'es' ? 'Ej: Escala validada Frosch & Kligman 1979' : 'e.g. Frosch & Kligman 1979'} className="text-sm min-h-[60px]" />
            </div>

            {/* Image upload */}
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
              <Label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">{lang === 'es' ? 'Imagen de referencia' : 'Reference image'}</Label>
              <p className="text-[11px] text-stone-500 mt-1 mb-2">
                {lang === 'es' ? 'Chart clínico con todos los niveles juntos. Máx 5 MB. JPG/PNG/WebP.' : 'Clinical chart with all levels. Max 5 MB.'}
              </p>
              <div className="flex items-center gap-3">
                {form.file_id ? (
                  <button
                    type="button"
                    onClick={() => setShowImagePreview({ scale_id: editing?.scale_id, file_id: form.file_id, name: form.name, description: form.description })}
                    className="w-20 h-20 border border-stone-200 bg-white rounded-sm overflow-hidden cursor-zoom-in"
                  >
                    {editing && (
                      <img
                        src={`${API_URL}/api/public/derm-scales/${editing.scale_id}/image`}
                        alt="ref"
                        className="w-full h-full object-contain"
                      />
                    )}
                    {!editing && <ImageIcon size={20} className="text-stone-400 m-auto" />}
                  </button>
                ) : (
                  <div className="w-20 h-20 bg-white border-2 border-dashed border-stone-300 rounded-sm flex items-center justify-center">
                    <ImageIcon size={20} className="text-stone-300" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 text-xs bg-white border border-stone-300 hover:bg-stone-50 rounded-sm px-3 py-1.5 cursor-pointer">
                    <Upload size={12} />
                    {uploading ? '...' : (form.file_id ? (lang === 'es' ? 'Reemplazar imagen' : 'Replace image') : (lang === 'es' ? 'Subir imagen' : 'Upload image'))}
                    <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="derm-scale-upload-input" disabled={uploading} />
                  </label>
                  {form.file_id && (
                    <Button variant="ghost" size="sm" type="button" className="ml-2 text-[11px] h-7 text-red-500" onClick={() => setForm(p => ({ ...p, file_id: null }))}>
                      {lang === 'es' ? 'Quitar' : 'Remove'}
                    </Button>
                  )}
                  {editing && form.file_id && (
                    <p className="text-[10px] text-stone-400 mt-1">
                      {lang === 'es' ? 'Para ver la imagen subida nueva, guarda primero y recarga.' : 'Save first to view the new image.'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* URS-F510: Toggle modo de respuesta (seleccion vs numerica) */}
            <div className="space-y-2 border border-stone-200 bg-stone-50 rounded-sm px-3 py-2.5">
              <Label className="text-xs font-semibold text-stone-700">{lang === 'es' ? 'Tipo de respuesta' : 'Response type'}</Label>
              <div className="flex gap-4 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="response_mode"
                    value="select"
                    checked={form.response_mode !== 'numeric'}
                    onChange={() => setForm(p => ({ ...p, response_mode: 'select' }))}
                    data-testid="derm-scale-mode-select"
                    className="accent-[#1f3e80]"
                  />
                  <span>{lang === 'es' ? 'Selección de niveles' : 'Level selection'}</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="response_mode"
                    value="numeric"
                    checked={form.response_mode === 'numeric'}
                    onChange={() => setForm(p => ({ ...p, response_mode: 'numeric' }))}
                    data-testid="derm-scale-mode-numeric"
                    className="accent-[#1f3e80]"
                  />
                  <span>{lang === 'es' ? 'Valor numérico libre' : 'Free numeric value'}</span>
                </label>
              </div>
              {form.response_mode === 'numeric' && (
                <div className="pt-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Texto del campo (opcional)' : 'Field prompt (optional)'}</Label>
                      <Input
                        value={form.numeric_prompt}
                        onChange={e => setForm(p => ({ ...p, numeric_prompt: e.target.value }))}
                        placeholder={lang === 'es' ? 'Introduce el valor' : 'Enter value'}
                        data-testid="derm-scale-numeric-prompt"
                        className="text-xs h-7"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Unidad (opcional)' : 'Unit (optional)'}</Label>
                      <Input
                        value={form.numeric_unit}
                        onChange={e => setForm(p => ({ ...p, numeric_unit: e.target.value }))}
                        placeholder={lang === 'es' ? 'cm, °, mm, %, etc.' : 'cm, °, mm, %, etc.'}
                        data-testid="derm-scale-numeric-unit"
                        className="text-xs h-7"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-500">
                    {lang === 'es'
                      ? 'En modo numérico se ignoran los niveles de abajo. En escalas compuestas, los valores numéricos NO suman al total.'
                      : 'In numeric mode the levels below are ignored. In composite scales, numeric values do NOT contribute to the total.'}
                  </p>
                </div>
              )}
            </div>

            {/* Options (solo si modo seleccion) */}
            {form.response_mode !== 'numeric' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{lang === 'es' ? 'Niveles de la escala' : 'Scale levels'} <span className="text-stone-400 text-xs">({form.options.length} / 10)</span></Label>
                <Button variant="outline" size="sm" onClick={addOption} disabled={form.options.length >= 10} data-testid="derm-scale-add-option-btn" className="text-xs gap-1 rounded-sm h-7">
                  <Plus size={11} />{lang === 'es' ? 'Añadir nivel' : 'Add level'}
                </Button>
              </div>
              <div className="space-y-1.5">
                {form.options.map((o, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white border border-stone-200 rounded-sm p-2" data-testid={`derm-scale-option-${idx}`}>
                    <Input
                      value={o.value}
                      onChange={e => updateOption(idx, 'value', e.target.value)}
                      placeholder="0"
                      className="text-xs h-7 w-14 text-center font-mono"
                      data-testid={`derm-option-value-${idx}`}
                    />
                    <Input
                      value={o.label}
                      onChange={e => updateOption(idx, 'label', e.target.value)}
                      placeholder={lang === 'es' ? 'Etiqueta corta (ej: Sin eritema)' : 'Short label'}
                      className="text-xs h-7 w-48"
                      data-testid={`derm-option-label-${idx}`}
                    />
                    <Input
                      value={o.description}
                      onChange={e => updateOption(idx, 'description', e.target.value)}
                      placeholder={lang === 'es' ? 'Descripción clínica detallada' : 'Detailed description'}
                      className="text-xs h-7 flex-1"
                      data-testid={`derm-option-desc-${idx}`}
                    />
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveOption(idx, -1)} disabled={idx === 0}><ArrowUp size={11} /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveOption(idx, 1)} disabled={idx === form.options.length - 1}><ArrowDown size={11} /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeOption(idx)} disabled={form.options.length <= 2}><X size={12} /></Button>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="derm-scale-save-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoriesDialog
        open={showCategoriesDialog}
        onClose={() => setShowCategoriesDialog(false)}
        categories={categories}
        onChanged={load}
        lang={lang}
      />

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onOpenChange={v => { if (!v) setConfirmDel(null); }}
        title={lang === 'es' ? 'Eliminar escala' : 'Delete scale'}
        description={lang === 'es' ? `¿Eliminar la escala "${confirmDel?.name || ''}"?` : `Delete "${confirmDel?.name || ''}"?`}
        onConfirm={() => handleDelete(confirmDel?.scale_id)}
      />
    </div>
  );
}

function CategoriesDialog({ open, onClose, categories, onChanged, lang }) {
  const [values, setValues] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValues(categories || []); }, [categories, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/derm-scale-categories`, { values }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Categorías actualizadas' : 'Updated');
      onChanged(); onClose();
    } catch (e) { toast.error(errText(e, 'Error')); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{lang === 'es' ? 'Editar categorías de escalas' : 'Edit categories'}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {values.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-sm px-2 py-1.5">
              <span className="text-sm flex-1 font-mono">{c}</span>
              <span className="text-xs text-stone-500">{categoryLabels[c] || ''}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => setValues(v => v.filter((_, i) => i !== idx))}><X size={11} /></Button>
            </div>
          ))}
          <div className="flex gap-1">
            <Input
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newCat.trim()) { setValues(v => [...v, newCat.trim().toLowerCase()]); setNewCat(''); } } }}
              placeholder={lang === 'es' ? 'Nueva categoría (slug)' : 'New category'}
              className="text-xs h-8"
            />
            <Button variant="outline" size="sm" onClick={() => { if (newCat.trim()) { setValues(v => [...v, newCat.trim().toLowerCase()]); setNewCat(''); } }}>
              <Plus size={12} />
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
